import { useState } from "react";
import { toast } from "sonner";
import { gestApi } from "../GestAuthContext";
import { CATEGORIE } from "./constants";

export default function SpeseLibereFlow({ onSaved }) {
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState(null); // null = before parse; array = review; "saved" = success
  const [cantieri, setCantieri] = useState([]);
  const [fileId, setFileId] = useState(null);
  const [sourceText, setSourceText] = useState("");
  const [savedInfo, setSavedInfo] = useState(null);

  const reset = () => { setText(""); setFile(null); setItems(null); setFileId(null); setSourceText(""); setSavedInfo(null); };

  const parse = async () => {
    if (busy) return;
    if (!text.trim() && !file) { toast.error("Scrivi qualcosa o carica un Excel"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      if (text.trim()) fd.append("text", text.trim());
      if (file) fd.append("file", file);
      const { data } = await gestApi.post("/documents/spese/parse", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      setItems((data.items || []).map((i) => ({ ...i, _key: Math.random().toString(36).slice(2) })));
      setCantieri(data.cantieri || []);
      setFileId(data.file_id || null);
      setSourceText(data.source_text || "");
      if (!data.items?.length) toast.warning("L'IA non ha trovato voci di spesa — riprova specificando importo e cantiere.");
      else toast.success(`${data.items.length} voce/i estratte — verifica e conferma.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore parsing IA");
    } finally { setBusy(false); }
  };

  const updateItem = (idx, k, v) => setItems((arr) => arr.map((it, i) => i === idx ? { ...it, [k]: v } : it));
  const removeItem = (idx) => setItems((arr) => arr.filter((_, i) => i !== idx));
  const addItem = () => setItems((arr) => [
    ...arr,
    { _key: Math.random().toString(36).slice(2), fornitore: null, importo: null, categoria: "altri", data: new Date().toISOString().slice(0, 10), descrizione: "", cantiere_id: null, cantiere_hint: null },
  ]);

  const confirm = async () => {
    if (!items?.length) return;
    // client-side quick validation
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      if (!it.cantiere_id) return toast.error(`Riga ${i + 1}: cantiere obbligatorio`);
      if (!it.importo || Number(it.importo) <= 0) return toast.error(`Riga ${i + 1}: importo obbligatorio`);
      if (!it.data) return toast.error(`Riga ${i + 1}: data obbligatoria`);
    }
    setBusy(true);
    try {
      const { data } = await gestApi.post("/documents/spese/confirm", {
        items: items.map(({ _key, ...rest }) => ({ ...rest, importo: Number(rest.importo) })),
        file_id: fileId,
        source_text: sourceText,
      });
      setSavedInfo(data);
      setItems("saved");
      toast.success(`${data.count} spesa/e salvata/e — cantieri aggiornati.`);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore salvataggio");
    } finally { setBusy(false); }
  };

  // --- Success view ---
  if (items === "saved" && savedInfo) {
    return (
      <div className="bg-white rounded-2xl p-8 border border-black/[0.04] shadow-sm text-center" data-testid="gest-spese-success">
        <div className="w-14 h-14 rounded-full bg-[#16a34a] mx-auto flex items-center justify-center mb-4">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
        </div>
        <div className="text-[18px] font-semibold text-[#0a1420]">{savedInfo.count} spesa/e registrata/e</div>
        <div className="text-[13px] text-[#6e6e73] mt-1 mb-5">I costi sono stati aggiunti ai cantieri collegati. Margine e semaforo ricalcolati.</div>
        <button onClick={reset} data-testid="gest-spese-new" className="px-5 py-2.5 bg-[#1D3F94] text-white rounded-lg text-[13px] font-semibold hover:bg-[#173277]">
          Aggiungi altre spese
        </button>
      </div>
    );
  }

  // --- Review view ---
  if (items && items.length >= 0) {
    return (
      <div className="bg-white rounded-2xl p-6 border border-black/[0.04] shadow-sm" data-testid="gest-spese-review">
        <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
          <div>
            <div className="text-[15px] font-semibold text-[#0a1420]">Verifica {items.length} spesa/e</div>
            <div className="text-[12px] text-[#6e6e73] mt-0.5">Correggi eventuali errori dell'IA — nulla viene scritto finché non premi «Salva tutte».</div>
          </div>
          <button onClick={reset} className="text-[12px] text-[#6e6e73] hover:text-[#0a1420]">↺ Ricomincia</button>
        </div>

        {items.length === 0 && (
          <div className="text-center py-8 text-[13px] text-[#6e6e73]">
            Nessuna voce estratta. Aggiungi manualmente una riga.
          </div>
        )}

        <div className="space-y-3">
          {items.map((it, idx) => (
            <div key={it._key} className="border border-black/[0.06] rounded-xl p-4 bg-[#f8fafc]" data-testid={`gest-spese-item-${idx}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="text-[11px] uppercase tracking-widest text-[#f59e0b] font-semibold">Riga {idx + 1}</div>
                <button onClick={() => removeItem(idx)} data-testid={`gest-spese-remove-${idx}`} className="text-[11px] text-red-500 hover:text-red-700">Rimuovi</button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-[11px] text-[#6e6e73] mb-1">Cantiere *</label>
                  <select value={it.cantiere_id || ""} onChange={(e) => updateItem(idx, "cantiere_id", e.target.value || null)} data-testid={`gest-spese-cantiere-${idx}`} className={`w-full border rounded-lg px-2.5 py-2 text-[13px] bg-white ${!it.cantiere_id ? "border-orange-400 bg-orange-50" : "border-black/10"}`}>
                    <option value="">— Seleziona —</option>
                    {cantieri.map((c) => <option key={c.id} value={c.id}>{`${c.nome} · ${c.cliente || ""}`}</option>)}
                  </select>
                  {it.cantiere_hint && !it.cantiere_id && (
                    <div className="text-[10px] text-orange-600 mt-1">Hint IA: «{it.cantiere_hint}»</div>
                  )}
                </div>
                <div>
                  <label className="block text-[11px] text-[#6e6e73] mb-1">Importo (€) *</label>
                  <input type="number" min="0.01" step="0.01" value={it.importo ?? ""} onChange={(e) => updateItem(idx, "importo", e.target.value === "" ? null : Number(e.target.value))} data-testid={`gest-spese-importo-${idx}`} className={`w-full border rounded-lg px-2.5 py-2 text-[13px] ${!it.importo ? "border-orange-400 bg-orange-50" : "border-black/10"}`} />
                </div>
                <div>
                  <label className="block text-[11px] text-[#6e6e73] mb-1">Data *</label>
                  <input type="date" value={it.data || ""} onChange={(e) => updateItem(idx, "data", e.target.value || null)} data-testid={`gest-spese-data-${idx}`} className={`w-full border rounded-lg px-2.5 py-2 text-[13px] ${!it.data ? "border-orange-400 bg-orange-50" : "border-black/10"}`} />
                </div>
                <div>
                  <label className="block text-[11px] text-[#6e6e73] mb-1">Categoria</label>
                  <select value={it.categoria || "altri"} onChange={(e) => updateItem(idx, "categoria", e.target.value)} className="w-full border border-black/10 rounded-lg px-2.5 py-2 text-[13px] bg-white">
                    {CATEGORIE.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] text-[#6e6e73] mb-1">Fornitore</label>
                  <input type="text" value={it.fornitore || ""} onChange={(e) => updateItem(idx, "fornitore", e.target.value || null)} placeholder="es. IP Genova" className="w-full border border-black/10 rounded-lg px-2.5 py-2 text-[13px]" />
                </div>
                <div className="md:col-span-1">
                  <label className="block text-[11px] text-[#6e6e73] mb-1">Descrizione</label>
                  <input type="text" value={it.descrizione || ""} onChange={(e) => updateItem(idx, "descrizione", e.target.value)} placeholder="es. sopralluogo mattina" className="w-full border border-black/10 rounded-lg px-2.5 py-2 text-[13px]" />
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4 flex items-center justify-between gap-3 flex-wrap pt-4 border-t border-black/5">
          <button onClick={addItem} data-testid="gest-spese-add" className="text-[12px] px-3 py-1.5 border border-black/10 rounded-lg hover:bg-[#f5f7fb]">+ Aggiungi riga</button>
          <div className="flex gap-2">
            <button onClick={reset} disabled={busy} className="px-4 py-2 text-[13px] text-[#6e6e73] hover:text-[#0a1420]">Annulla</button>
            <button onClick={confirm} disabled={busy || items.length === 0} data-testid="gest-spese-confirm" className="px-5 py-2 bg-[#1D3F94] text-white rounded-lg text-[13px] font-semibold hover:bg-[#173277] disabled:opacity-40">
              {busy ? "Salvataggio…" : `Salva ${items.length} spesa/e`}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // --- Input view (text OR excel) ---
  return (
    <div className="bg-white rounded-2xl p-6 border border-black/[0.04] shadow-sm" data-testid="gest-spese-input">
      <div className="text-[13px] font-semibold text-[#0a1420] mb-2">Descrivi la spesa</div>
      <div className="text-[12px] text-[#6e6e73] mb-4">
        Scrivi in linguaggio naturale (es. «oggi per Genova ho speso 50 di sopralluogo e 35 di carburante»)
        <span className="hidden md:inline"> — l'IA leggerà una o più spese e le collegherà ai cantieri.</span>
      </div>

      <textarea
        rows={4}
        value={text}
        onChange={(e) => setText(e.target.value)}
        data-testid="gest-spese-text"
        placeholder="es. Ieri per Villa Rossi ho anticipato 45 euro di carburante andando col furgone, e 20 di pranzo con il posatore..."
        maxLength={4000}
        className="w-full border border-black/10 rounded-xl px-3.5 py-3 text-[14px] resize-none focus:border-[#f59e0b] outline-none"
      />
      <div className="text-[10px] text-[#6e6e73] mt-1 text-right">{text.length} / 4000</div>

      <div className="mt-5 flex items-center gap-3 flex-wrap">
        <div className="text-[12px] text-[#6e6e73]">…oppure carica un</div>
        <label className={`text-[12px] px-3 py-1.5 border rounded-lg cursor-pointer transition-colors ${file ? "border-[#f59e0b] bg-orange-50 text-[#0a1420]" : "border-black/10 hover:bg-[#f5f7fb]"}`}>
          <input
            type="file"
            accept=".xlsx,.xls,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            data-testid="gest-spese-file"
            className="sr-only"
          />
          {file ? `📎 ${file.name}` : "Excel (.xlsx) con più righe"}
        </label>
        {file && <button onClick={() => setFile(null)} className="text-[11px] text-red-500">Rimuovi</button>}
      </div>

      <div className="mt-5 pt-4 border-t border-black/5 flex items-center justify-between gap-3">
        <div className="text-[11px] text-[#6e6e73]">L'IA Gemini leggerà il testo o il foglio Excel.</div>
        <button
          type="button"
          onClick={parse}
          disabled={busy || (!text.trim() && !file)}
          data-testid="gest-spese-parse"
          className="px-5 py-2.5 bg-[#1D3F94] text-white rounded-lg text-[13px] font-semibold hover:bg-[#173277] disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {busy ? (<span className="inline-flex items-center gap-2"><span className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" /> Analisi in corso…</span>) : "Analizza con IA →"}
        </button>
      </div>
    </div>
  );
}


